import { useEffect, useState, type JSX } from "react";
import { CheckCircle } from "@untitledui/icons";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Modal } from "@/components/application/modals/modal-controlled";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import {
  BUDGET_APPROVE_PHRASE,
  formatBudgetMonth,
  formatEur,
  formatKc,
  isApprovePhraseValid,
} from "../../schemas/budget";
import { useBreakpoint } from "../../hooks/useBreakpoint";

/**
 * UC-002 BudgetApproveDialog — 3-stupňový Confirm Dialog s desktop guardem
 * (Phase 16, REQ-UI-016 + REQ-UI-017 + REQ-BIZ-009 + princip #3).
 *
 * 3 stupně:
 *   1. **Summary review**  — admin vidí měsíc + Středisko + celkové částky;
 *      buttons "Zpět" / "Pokračovat".
 *   2. **Immutable warning** — princip #2 source-of-truth banner;
 *      buttons "Zpět" / "Rozumím, pokračovat".
 *   3. **Phrase confirm**   — text input s exact-match phrase
 *      "potvrzuji nevratně" (case-insensitive, trim); buttons "Zpět" /
 *      "Schválit (nevratně)".
 *
 * Desktop guard:
 *   - Pokud `breakpoint === "mobile"` → render warning Alert + disable
 *     buttony (REQ-UI-017 — accidental-tap risk mitigation per ANALYZE §5.5).
 *
 * Approve mutation se spustí v onConfirm callback (mutation hook v parent).
 */

export interface BudgetApproveDialogProps {
  open: boolean;
  /** Měsíc YYYY-MM. */
  month: string;
  billingGroupCode: string;
  totalAmountKc: string;
  totalAmountEur: string;
  eurAdvisoryFlag: boolean;
  itemCount: number;
  unassignedCount: number;
  /** Pokud TRUE → submitting state (disable buttons + zobraz "Schvaluji…"). */
  submitting?: boolean;
  /** Volitelná chybová hláška z mutation (např. 422/409/503). */
  submitError?: string | null;
  /** Pokud TRUE → vynutí desktop-only chování (testy). */
  forceMobile?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

type Step = 1 | 2 | 3;

export function BudgetApproveDialog(props: BudgetApproveDialogProps) {
  const {
    open,
    month,
    billingGroupCode,
    totalAmountKc,
    totalAmountEur,
    eurAdvisoryFlag,
    itemCount,
    unassignedCount,
    submitting,
    submitError,
    forceMobile,
    onCancel,
    onConfirm,
  } = props;

  const breakpoint = useBreakpoint();
  const isMobile = forceMobile === true || breakpoint === "mobile";

  const [step, setStep] = useState<Step>(1);
  const [phrase, setPhrase] = useState<string>("");

  // Reset stepper + phrase při (re-)open.
  useEffect(() => {
    if (open) {
      setStep(1);
      setPhrase("");
    }
  }, [open]);

  const phraseValid = isApprovePhraseValid(phrase);
  const hasBlockingUnassigned = unassignedCount > 0;

  function renderBody(): JSX.Element {
    if (isMobile) {
      return (
        <InlineAlert variant="warning" title="Vyžaduje desktop">
          Schválení rozpočtu vyžaduje desktop kvůli prevenci náhodného
          potvrzení. Otevřete tuto stránku v prohlížeči na počítači.
        </InlineAlert>
      );
    }
    if (hasBlockingUnassigned) {
      return (
        <InlineAlert variant="error" title="Nepřiřazené SPZ blokují schválení">
          Tento rozpočet má {unassignedCount} nepřiřazených SPZ — nejprve je
          přiřaďte přes "Vyřešit nepřiřazené SPZ".
        </InlineAlert>
      );
    }
    if (step === 1) {
      return (
        <div className="budget-approve__step" data-testid="budget-approve-step-1">
          <p>
            Připravujete se schválit rozpočet pro
            <strong> Středisko {billingGroupCode}</strong> · <strong>{formatBudgetMonth(month)}</strong>.
          </p>
          <dl className="budget-approve__summary">
            <div>
              <dt>Celkem CZK</dt>
              <dd data-testid="budget-approve-total-kc">{formatKc(totalAmountKc)}</dd>
            </div>
            <div>
              <dt>Celkem EUR</dt>
              <dd data-testid="budget-approve-total-eur">{formatEur(totalAmountEur)}</dd>
            </div>
            <div>
              <dt>Položek (SPZ)</dt>
              <dd>{itemCount}</dd>
            </div>
            {eurAdvisoryFlag && (
              <div>
                <dt>EUR advisory</dt>
                <dd>Ano — měsíc obsahuje EUR transakce.</dd>
              </div>
            )}
          </dl>
        </div>
      );
    }
    if (step === 2) {
      return (
        <div className="budget-approve__step" data-testid="budget-approve-step-2">
          <InlineAlert variant="warning" title="Nevratný snapshot">
            Po schválení se rozpočet stane <strong>neměnným snímkem</strong>.
            Žádné další úpravy SPZ ani dodatečné transakce neproniknou do
            tohoto rozpočtu — vrátit jej do draftu vyžaduje audit-logovanou
            akci "Vrátit k opravě".
          </InlineAlert>
          <p className="budget-approve__step-note">
            Rozklad zůstane dostupný pro drill-down všem členům skupiny
            i po schválení.
          </p>
        </div>
      );
    }
    return (
      <div className="budget-approve__step" data-testid="budget-approve-step-3">
        <p>
          Pro nevratné schválení napište do políčka přesnou frázi
          <strong> „{BUDGET_APPROVE_PHRASE}"</strong> (bez uvozovek).
        </p>
        <Input
          label="Potvrzovací fráze"
          hint={'Velká/malá písmena ignorujeme; diakritika musí sedět ("ně").'}
          isRequired
          value={phrase}
          onChange={setPhrase}
          placeholder={BUDGET_APPROVE_PHRASE}
          autoComplete="off"
          data-testid="budget-approve-phrase-input"
        />
        <p
          className="budget-approve__phrase-hint"
          data-testid="budget-approve-phrase-status"
        >
          {phraseValid ? "✓ Fráze odpovídá." : "× Fráze zatím neodpovídá."}
        </p>
        {submitError && (
          <InlineAlert variant="error" title="Chyba při schvalování">
            {submitError}
          </InlineAlert>
        )}
      </div>
    );
  }

  function renderFooter(): JSX.Element {
    if (isMobile) {
      return (
        <Button color="secondary" onPress={onCancel} data-testid="budget-approve-mobile-cancel">
          Zavřít
        </Button>
      );
    }
    if (hasBlockingUnassigned) {
      return (
        <Button color="secondary" onPress={onCancel} data-testid="budget-approve-blocked-cancel">
          Zavřít
        </Button>
      );
    }
    if (step === 1) {
      return (
        <>
          <Button color="secondary" onPress={onCancel} isDisabled={submitting}>
            Zrušit
          </Button>
          <Button
            color="primary"
            onPress={() => setStep(2)}
            data-testid="budget-approve-next-1"
          >
            Pokračovat
          </Button>
        </>
      );
    }
    if (step === 2) {
      return (
        <>
          <Button color="secondary" onPress={() => setStep(1)} isDisabled={submitting}>
            Zpět
          </Button>
          <Button
            color="primary"
            onPress={() => setStep(3)}
            data-testid="budget-approve-next-2"
          >
            Rozumím, pokračovat
          </Button>
        </>
      );
    }
    return (
      <>
        <Button color="secondary" onPress={() => setStep(2)} isDisabled={submitting}>
          Zpět
        </Button>
        <Button
          color="primary-destructive"
          onPress={onConfirm}
          isDisabled={!phraseValid || submitting}
          data-testid="budget-approve-confirm"
        >
          {submitting ? "Schvaluji…" : "Schválit (nevratně)"}
        </Button>
      </>
    );
  }

  return (
    <Modal
      open={open}
      title="Schválit rozpočet"
      icon={CheckCircle}
      iconColor="success"
      onClose={onCancel}
      footer={renderFooter()}
    >
      {renderBody()}
    </Modal>
  );
}
